import { isManageUsersItemsSeen } from '@jusbrasil/topbar'
import { useUserSession } from '@jusbrasil-web/shared-user-session'
import dynamic from 'next/dynamic'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import {
  CompleteSignupNudgeContext,
  CompleteSignupNudgeContextValue,
} from './complete-signup-nudge-context'
import {
  COMPLETE_SIGNUP_NUDGE_AUTO_OPEN_DELAY_MS,
  COMPLETE_SIGNUP_NUDGE_VARIANTS,
} from './constants'
import { isCompleteSignupNudgeEligible, shouldSkipPhoneStep } from './eligibility'
import { isCompleteSignupNudgeSnoozed, snoozeCompleteSignupNudge } from './frequency-cap'
import { useCompleteSignupNudgeExperiment } from './use-complete-signup-nudge-experiment'
import { useNudgeViewer } from './use-nudge-viewer'
import { isCompleteSignupNudgeShown } from './variant'

// Lazy-loaded so the modal's steps/illustrations ship in their own chunk, fetched only when shown.
const CompleteSignupModal = dynamic(
  () => import('./complete-signup-modal').then((mod) => mod.CompleteSignupModal),
  { ssr: false }
)

export function CompleteSignupNudgeProvider({
  children,
  basePath,
}: {
  children: ReactNode
  // App proxy prefix (e.g. /web/docview); apps are served under /web/<app>/*, so the API fetch must
  // be prefixed or it 404s in prod. Required so an app can't silently fall back to the root path.
  basePath: string
}) {
  const session = useUserSession()
  const loggedIn = session?.pid != null

  const { fetchViewer, viewer } = useNudgeViewer()

  // The session only gates whether to query; identity comes from the viewer profile below.
  useEffect(() => {
    if (loggedIn) fetchViewer()
  }, [loggedIn, fetchViewer])

  // Single pid source — the viewer profile the nudge acts on, so eligibility, snooze and the
  // modal all align on the same identity (the session pid may differ on multi-profile accounts).
  const pid = viewer?.pid ?? null
  const snoozed = pid != null && isCompleteSignupNudgeSnoozed(pid)
  const eligible = isCompleteSignupNudgeEligible(viewer)
  // Yield the whole experiment (exposure included) to the topbar's manage-users popover, which
  // claims the same avatar — entering only after it has been seen keeps variant A undiluted.
  const manageUsersPopoverSeen = isManageUsersItemsSeen(viewer?.isB2BSelfService ?? false)
  const { variant } = useCompleteSignupNudgeExperiment(
    eligible && !snoozed && manageUsersPopoverSeen,
    basePath
  )

  const isPopover = variant === COMPLETE_SIGNUP_NUDGE_VARIANTS.POPOVER
  const isAutoModal = variant === COMPLETE_SIGNUP_NUDGE_VARIANTS.AUTO_MODAL

  const [open, setOpen] = useState(false)
  const [popoverDismissed, setPopoverDismissed] = useState(false)

  useEffect(() => {
    if (!isAutoModal) return undefined
    const timer = setTimeout(() => setOpen(true), COMPLETE_SIGNUP_NUDGE_AUTO_OPEN_DELAY_MS)
    return () => clearTimeout(timer)
  }, [isAutoModal])

  const snooze = useCallback(() => {
    if (pid != null) snoozeCompleteSignupNudge(pid, new Date().toISOString())
  }, [pid])

  const openModal = useCallback(() => {
    setPopoverDismissed(true)
    setOpen(true)
  }, [])

  const dismiss = useCallback(() => {
    setPopoverDismissed(true)
    snooze()
  }, [snooze])

  // Closing the modal without completing (X / esc / outside / "preencher depois") snoozes too,
  // so the frequency cap applies — otherwise variant B would reopen on every pageview.
  const dismissModal = useCallback(() => {
    snooze()
    setOpen(false)
  }, [snooze])

  const contextValue = useMemo<CompleteSignupNudgeContextValue>(
    () => ({ showPopover: isPopover && !popoverDismissed && !open, openModal, dismiss }),
    [isPopover, popoverDismissed, open, openModal, dismiss]
  )

  const modalViewer = viewer
    ? { pid: viewer.pid, cpf: viewer.cpf, birthDate: viewer.birthDate }
    : null

  return (
    <CompleteSignupNudgeContext.Provider value={contextValue}>
      {children}
      {isCompleteSignupNudgeShown(variant) && (
        <CompleteSignupModal
          open={open}
          onDismiss={dismissModal}
          viewer={modalViewer}
          firstName={viewer?.fullname?.split(' ')[0]}
          skipPhoneStep={shouldSkipPhoneStep(viewer)}
          onSkip={dismissModal}
          onClose={() => setOpen(false)}
        />
      )}
    </CompleteSignupNudgeContext.Provider>
  )
}
